import { useEffect, useState } from "react";

import { updateWeeklyEACDirectLaborAPI } from "../../../../api/financial-management-api";
import { recalculateBucketBurdenCost } from "./weeklyEACCalculations";
import { recalculateOdcBucketBurdenCost } from "./weeklyEACODCCalculations";

const cloneEmployeeDetails = (data) =>
  data?.employeeDetails ? JSON.parse(JSON.stringify(data.employeeDetails)) : [];

const cloneOdcDetails = (data) =>
  data?.odcDetails ? JSON.parse(JSON.stringify(data.odcDetails)) : [];

const cloneCocrDetails = (data) =>
  data?.cocrDetails ? JSON.parse(JSON.stringify(data.cocrDetails)) : [];

// Owns the editable Direct Labor / Other Direct Costs / Credit for Over Ceiling Rate (COCR)
// snapshots and the shared Edit/Save/Cancel flow — all three save together via the same PUT call,
// so a single Edit toggle governs all of them. COCR is now a stored CLIN-level value per week
// (cocrDetails[], matched by weekStartDate/weekEndDate, not per-employee) rather than something
// computed or manually overridden on top of weeklyTotals — editing it mutates its own bucket
// directly, the same pattern as DL hours / ODC unburden cost.
export const useWeeklyEACEditableData = ({
  contractId,
  clin,
  version,
  directLaborData,
  detailedViewRatesData,
  onSaved,
}) => {
  const [employeeDetails, setEmployeeDetails] = useState([]);
  const [odcDetails, setOdcDetails] = useState([]);
  const [cocrDetails, setCocrDetails] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [msgType, setMsgType] = useState();
  const [msgText, setMsgText] = useState();
  const [showMessage, setShowMessage] = useState(false);

  // A new CLIN/version fetch replaces the editable snapshot and drops out of edit mode.
  useEffect(() => {
    setEmployeeDetails(cloneEmployeeDetails(directLaborData));
    setOdcDetails(cloneOdcDetails(directLaborData));
    setCocrDetails(cloneCocrDetails(directLaborData));
    setIsEdit(false);
  }, [directLaborData]);

  const handleCocrValueChange = (weekStartDate, value) => {
    setCocrDetails((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const bucket = next.find((item) => item.weekStartDate === weekStartDate);
      if (!bucket) return prev;
      bucket.cocrValue = value === "" ? "" : Number(value);
      return next;
    });
  };

  const handleHoursChange = (dlId, weekStartDate, value) => {
    setEmployeeDetails((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const employee = next.find((emp) => emp.dlId === dlId);
      const bucket = employee?.weeklyBuckets.find(
        (item) => item.weekStartDate === weekStartDate,
      );
      if (!bucket) return prev;
      bucket.hours = value === "" ? "" : Number(value);
      bucket.burdenCost = recalculateBucketBurdenCost(bucket);
      return next;
    });
  };

  const handleUnburdenCostChange = (odcId, weekStartDate, value) => {
    setOdcDetails((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const odc = next.find((item) => item.odcId === odcId);
      const bucket = odc?.weeklyBuckets.find((item) => item.weekStartDate === weekStartDate);
      if (!bucket) return prev;
      bucket.unburdenCost = value === "" ? "" : Number(value);
      bucket.burdenCost = recalculateOdcBucketBurdenCost(bucket, odc, detailedViewRatesData?.data);
      return next;
    });
  };

  const isMessageClosed = (val) => setShowMessage(!val);

  const clinDetails = directLaborData?.clinDetails || {};
  const originalWeeklyTotals = directLaborData?.weeklyTotals || [];

  const handleSave = () => {
    setIsSaving(true);
    const body = {
      clinDetails,
      employeeDetails,
      odcDetails,
      cocrDetails,
      weeklyTotals: originalWeeklyTotals,
    };
    updateWeeklyEACDirectLaborAPI(contractId, clin, version, body).then((res) => {
      setIsSaving(false);
      if (res && res.status === 200) {
        setMsgType("success");
        setMsgText("Data updated successfully");
        setShowMessage(true);
        setIsEdit(false);
        if (onSaved) onSaved();
      } else {
        setMsgType("error");
        setMsgText(res?.message || "Failed to update Weekly EAC data");
        setShowMessage(true);
      }
    });
  };

  // Discards every edit made since Edit was clicked and reverts to the last-fetched snapshot.
  const handleCancel = () => {
    setEmployeeDetails(cloneEmployeeDetails(directLaborData));
    setOdcDetails(cloneOdcDetails(directLaborData));
    setCocrDetails(cloneCocrDetails(directLaborData));
    setIsEdit(false);
  };

  return {
    clinDetails,
    originalWeeklyTotals,
    employeeDetails,
    odcDetails,
    cocrDetails,
    isEdit,
    setIsEdit,
    isSaving,
    msgType,
    msgText,
    showMessage,
    isMessageClosed,
    handleCocrValueChange,
    handleHoursChange,
    handleUnburdenCostChange,
    handleSave,
    handleCancel,
  };
};
